import { readyToRequestMicPermissionsAtom } from '@/atoms/audioAtoms';
import {
  IS_TELEHEALTH_ALLOW_CURRENT_TAB_ENABLED,
  isMediaRecorderTimesliceEnabled,
} from '@/features/flags';
import { USE_TRANSCRIPTION_SERVICE_V2 } from '@/features/flags/legacy-flags';
import { resolveRecordingStoreV2Enabled } from '@/features/flags/recording-store-v2';
import { GET_USER, getCurrentUser } from '@/hooks/query/current-user-cache';
import {
  appendEngineRecordingAttemptCheckpoint,
  cancelEngineRecordingAttempt,
  finishEngineRecordingAttemptFailure,
} from '@/lib/telemetry/recordingStartTracking';
import { isTauri } from '@/tauri/utils';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { configureCapture } from '@heidi/capture/configure';
import { logger } from '@heidi/runtime/logger';
import { getDefaultStore } from 'jotai';

// SCR-6270: the recording slice needs the user ref id, which may not be in the
// query cache yet when recording starts. Resolve immediately on a cache hit,
// otherwise wait for the user query to populate, capped by a timeout so a
// never-arriving user cannot block the caller. Every listener is torn down on
// settle. The engine sees only `resolveCurrentUserRefId()` — react-query and
// the raw query key stay app-side.
const USER_REF_RESOLVE_TIMEOUT_MS = 5000;

const resolveCurrentUserRefId = (): Promise<string | undefined> =>
  new Promise((resolve) => {
    const immediate = getCurrentUser()?.user_ref_id;
    if (immediate) {
      resolve(immediate);
      return;
    }
    const cleanups: Array<() => void> = [];
    const settle = (value: string | undefined) => {
      cleanups.forEach((cleanup) => cleanup());
      resolve(value);
    };
    const timer = setTimeout(
      () => settle(getCurrentUser()?.user_ref_id),
      USER_REF_RESOLVE_TIMEOUT_MS
    );
    cleanups.push(() => clearTimeout(timer));
    const unsubscribe = reactQueryClient.getQueryCache().subscribe((event) => {
      const key = (event as { query?: { queryKey?: readonly unknown[] } }).query
        ?.queryKey?.[0];
      if (key !== GET_USER) return;
      const refId = getCurrentUser()?.user_ref_id;
      if (refId) settle(refId);
    });
    cleanups.push(unsubscribe);
  });

// Capture bootstrap wiring (OpenSpec monorepo-kit-capture P2c · ARCFE-711).
// Side-effect import, evaluated from `_app.tsx` immediately after polyfills
// and BEFORE `@/lib/utils` — i.e. before every engine-reachable import — so
// module-evaluation-time engine work (store construction, `init()`, slice
// logger bindings) already sees today's app implementations through the
// package facades. This file supplies the exact phase-S implementations the
// deleted source configuration defaults used; behaviour is byte-identical.
//
// Import-graph constraint (tested): this module and everything it transitively
// imports reach only `@heidi/capture/configure` — never the package root, the
// stores, recorders, or Dexie.
//
// Lifecycle split (design D1): the five synchronous getters evaluate per call
// and observe later flag changes; `resolveLedgerEnabled` is read only until
// the pipeline-mode coordinator pins `legacy | ledger` once per page load —
// the app resolver (`resolveRecordingStoreV2Enabled`) keeps its Ready/Error
// handler cleanup and 3-second readiness fallback app-side.
configureCapture({
  // The runtime logger structurally satisfies the kit-owned CaptureLogger
  // contract; wired directly from @heidi/runtime/logger (not through the
  // @/utils/logger/logger shim — design Q6, dissolved 2026-07-21).
  loggerBase: logger,
  flags: {
    // release-default-telehealth-mode shipped at 100% — always on.
    isDefaultTelehealthModeEnabled: () => true,
    isTelehealthAllowCurrentTabEnabled: IS_TELEHEALTH_ALLOW_CURRENT_TAB_ENABLED,
    useTranscriptionServiceV2: USE_TRANSCRIPTION_SERVICE_V2,
    isMediaRecorderTimesliceEnabled,
  },
  userContext: {
    getCurrentUser,
    resolveCurrentUserRefId,
  },
  // The engine's port has no key: it reports to the start or resume attempt
  // bound around the engine start, so only that press's attempt is touched.
  perf: {
    appendStartRecordingCheckpoint: ({ checkpointName, metadata }) =>
      appendEngineRecordingAttemptCheckpoint(checkpointName, metadata),
    cancelStartRecordingTracking: cancelEngineRecordingAttempt,
    finishStartRecordingTrackingFailure: finishEngineRecordingAttemptFailure,
  },
  hostInputs: {
    // Bridge over the app's mic-readiness atom. `getDefaultStore()` is
    // correct because the app runs provider-less default-store mode
    // (precedent: posthog-provider.tsx). Resolved lazily inside the closures
    // so importing this module never touches jotai state.
    micReadiness: {
      getSnapshot: () => getDefaultStore().get(readyToRequestMicPermissionsAtom),
      subscribe: (listener) =>
        getDefaultStore().sub(readyToRequestMicPermissionsAtom, listener),
      // The write half, for the mic test: pressing "Test microphone" is the
      // same user-intent signal opening the mic dropdown is, and the dropdown
      // may never have been opened.
      setReady: (ready) => getDefaultStore().set(readyToRequestMicPermissionsAtom, ready),
    },
    isNativeCaptureHost: isTauri,
  },
  // The app owns OpenFeature readiness, the raw key/domain, and the timeout;
  // Capture's pipeline-mode coordinator owns coalescing and the per-page pin.
  persistence: {
    resolveLedgerEnabled: resolveRecordingStoreV2Enabled,
  },
});
